/**
 * CEO Dashboard - Marketing Command Center
 *
 * Enterprise features:
 * - Real-time metrics polling (30s)
 * - AI activity feed with live updates
 * - Multi-company portfolio view
 * - Budget control and monitoring
 * - Approval queue management
 */

import { useState, useMemo, useCallback } from 'react';
import Button from '../../components/common/Button';
import { marketingApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './LazyMarketing.css';

// Constants
const METRICS_REFRESH_INTERVAL = 30000; // 30 seconds
const AI_FEED_REFRESH_INTERVAL = 15000; // 15 seconds

const CEODashboard = () => {
  const { showToast } = useToast();
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch companies list
  const {
    data: companiesData,
    isLoading: loadingCompanies,
  } = useQuery(
    () => marketingApi.companies.list(),
    {
      key: 'marketing:companies',
      cacheTTL: 300000, // 5 minutes
    }
  );

  // Fetch hero metrics with polling
  const {
    data: metricsData,
    isLoading: loadingMetrics,
    refetch: refetchMetrics,
  } = useQuery(
    () => marketingApi.heroMetrics({ companyId: selectedCompany === 'all' ? undefined : selectedCompany }),
    {
      key: `marketing:metrics:${selectedCompany}`,
      cacheTTL: 15000,
      refetchInterval: METRICS_REFRESH_INTERVAL,
    }
  );

  // Fetch AI activity feed with faster polling
  const {
    data: aiFeedData,
    isLoading: loadingFeed,
    refetch: refetchFeed,
  } = useQuery(
    () => marketingApi.aiFeed({ companyId: selectedCompany === 'all' ? undefined : selectedCompany, limit: 10 }),
    {
      key: `marketing:aiFeed:${selectedCompany}`,
      cacheTTL: 10000,
      refetchInterval: AI_FEED_REFRESH_INTERVAL,
    }
  );

  // Fetch budget overview
  const {
    data: budgetData,
    isLoading: loadingBudget,
  } = useQuery(
    () => marketingApi.budget.overview(selectedCompany === 'all' ? undefined : selectedCompany),
    {
      key: `marketing:budget:${selectedCompany}`,
      cacheTTL: 60000,
    }
  );

  // Fetch approval queue
  const {
    data: queueData,
    isLoading: loadingQueue,
    refetch: refetchQueue,
  } = useQuery(
    () => marketingApi.approvalQueue.list({ status: 'pending' }),
    {
      key: 'marketing:approvalQueue',
      cacheTTL: 30000,
    }
  );

  // Approve mutation
  const approveMutation = useMutation(
    (id) => marketingApi.approvalQueue.approve(id),
    {
      onSuccess: () => {
        showToast('Item approved', 'success');
        refetchQueue();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to approve', 'error');
      },
    }
  );

  // Extract data
  const companies = useMemo(() => {
    const data = companiesData || {};
    return data.companies || data.data || [];
  }, [companiesData]);

  const selectedCompanyName = useMemo(() => {
    if (selectedCompany === 'all') return 'All Companies';
    const company = companies.find(c => c.id === selectedCompany);
    return company?.name || 'Unknown';
  }, [selectedCompany, companies]);

  const heroMetrics = useMemo(() => {
    const data = metricsData || {};
    const metrics = data.metrics || data.data || [];

    // Default metrics structure if API returns different format
    if (metrics.length === 0) {
      return [
        { icon: '$', value: formatCurrency(data.totalRevenue || 0), label: 'Total Revenue', change: formatChange(data.revenueChange) },
        { icon: '⚡', value: formatNumber(data.leadsGenerated || 0), label: 'Leads Generated', change: formatChange(data.leadsChange) },
        { icon: '◉', value: formatCurrency(data.adSpend || 0), label: `Ad Spend (${data.budgetUsed || 0}%)`, change: `$${formatNumber(data.totalBudget || 0)} budget` },
        { icon: '↗', value: `${data.roi || 0}x`, label: 'Portfolio ROI', change: formatChange(data.roiChange, 'x') },
        { icon: '◈', value: formatNumber(data.aiActions || 0), label: 'AI Actions Today', change: '24/7 active' },
        { icon: '✦', value: formatNumber(data.contentPublished || 0), label: 'Content Published', change: formatChange(data.contentChange) },
      ];
    }

    return metrics;
  }, [metricsData]);

  const aiFeed = useMemo(() => {
    const data = aiFeedData || {};
    return data.feed || data.activities || data.data || [];
  }, [aiFeedData]);

  const budget = useMemo(() => {
    const data = budgetData || {};
    return {
      spent: data.spent || data.totalSpent || 0,
      total: data.total || data.totalBudget || 0,
      breakdown: data.breakdown || data.channels || [],
    };
  }, [budgetData]);

  const budgetPercentage = useMemo(() => {
    return budget.total > 0 ? Math.round((budget.spent / budget.total) * 100) : 0;
  }, [budget]);

  const approvalQueue = useMemo(() => {
    const data = queueData || {};
    return data.items || data.queue || data.data || [];
  }, [queueData]);

  // Handlers
  const handleCompanySelect = useCallback((companyId) => {
    setSelectedCompany(companyId);
    setShowDropdown(false);
  }, []);

  const handleApprove = useCallback((id) => {
    approveMutation.mutate(id);
  }, [approveMutation]);

  const handleRefresh = useCallback(() => {
    refetchMetrics();
    refetchFeed();
    refetchQueue();
    showToast('Dashboard refreshed', 'success');
  }, [refetchMetrics, refetchFeed, refetchQueue, showToast]);

  // Helpers
  function formatCurrency(amount) {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(amount >= 10000 ? 0 : 1)}K`;
    return `$${amount.toLocaleString()}`;
  }

  function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  }

  function formatChange(value, suffix = '%') {
    if (value === null || value === undefined) return '';
    const sign = value >= 0 ? '↑' : '↓';
    return `${sign} ${Math.abs(value)}${suffix}`;
  }

  function getStatusClass(status) {
    switch (status) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'info': return 'info';
      default: return 'info';
    }
  }

  function formatRelativeTime(timestamp) {
    if (!timestamp) return '';
    const now = Date.now();
    const diff = now - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  const isLoading = loadingMetrics && !metricsData;

  return (
    <div className="lm-page">
      {/* CEO Header */}
      <div className="ceo-header">
        <div className="ceo-header-left">
          <div className="ceo-logo">Lazy Marketing</div>
          <div className="company-selector">
            <button
              className="company-selector-btn"
              onClick={() => setShowDropdown(!showDropdown)}
              disabled={loadingCompanies}
            >
              <div className="company-selector-icon">🏢</div>
              <div className="company-selector-text">
                <div className="company-selector-name">{selectedCompanyName}</div>
                <div className="company-selector-count">
                  {loadingCompanies ? 'Loading...' : `${companies.length} active companies`}
                </div>
              </div>
              <span className="company-selector-arrow">▼</span>
            </button>
            {showDropdown && (
              <div className="company-dropdown">
                <div className="company-dropdown-search">
                  <input type="text" placeholder="Search companies..." />
                </div>
                <div className="company-dropdown-list">
                  <div
                    className={`company-dropdown-item ${selectedCompany === 'all' ? 'active' : ''}`}
                    onClick={() => handleCompanySelect('all')}
                  >
                    <div className="company-item-logo all">🏢</div>
                    <div className="company-item-info">
                      <div className="company-item-name">All Companies</div>
                      <div className="company-item-metric">Portfolio view</div>
                    </div>
                  </div>
                  {companies.map(company => (
                    <div
                      key={company.id}
                      className={`company-dropdown-item ${selectedCompany === company.id ? 'active' : ''}`}
                      onClick={() => handleCompanySelect(company.id)}
                    >
                      <div className="company-item-logo">{company.logo || company.name?.substring(0, 2)}</div>
                      <div className="company-item-info">
                        <div className="company-item-name">{company.name}</div>
                        <div className="company-item-metric">{formatChange(company.change)} this week</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="ceo-header-right">
          <button className="ceo-refresh-btn" onClick={handleRefresh} title="Refresh">
            🔄
          </button>
          <div className="notification-bell">
            🔔
            {approvalQueue.length > 0 && (
              <span className="notification-badge">{approvalQueue.length}</span>
            )}
          </div>
          <div className="notification-bell">👤</div>
        </div>
      </div>

      {/* Context Bar */}
      <div className="ceo-context-bar">
        <div className="context-left">
          <span className="context-viewing">
            Viewing: <strong>{selectedCompanyName} ({companies.length})</strong>
          </span>
          <div className="time-selector">📅 Last 30 Days ▼</div>
        </div>
        <div className="system-health">
          <span className="health-dot"></span>
          All Systems Operational
        </div>
      </div>

      {/* Hero Metrics */}
      <div className="ceo-hero-metrics">
        {isLoading ? (
          // Skeleton loading
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="hero-metric-card skeleton">
              <div className="skeleton-icon"></div>
              <div className="skeleton-value"></div>
              <div className="skeleton-label"></div>
            </div>
          ))
        ) : (
          heroMetrics.map((metric, index) => (
            <div key={index} className="hero-metric-card">
              <div className="hero-metric-icon">{metric.icon}</div>
              <div className="hero-metric-value">{metric.value}</div>
              <div className="hero-metric-label">{metric.label}</div>
              <div className={`hero-metric-change ${metric.change?.includes('↑') ? 'positive' : metric.change?.includes('↓') ? 'negative' : ''}`}>
                {metric.change}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Widget Grid */}
      <div className="ceo-widget-grid">
        {/* AI Activity Feed */}
        <div className="ceo-widget widget-ai">
          <div className="ceo-widget-header">
            <div className="ceo-widget-title">🤖 AI Activity Feed</div>
            <span className="ceo-widget-action" onClick={refetchFeed}>Refresh</span>
          </div>
          <div className="ceo-widget-body">
            {loadingFeed && aiFeed.length === 0 ? (
              <div className="ai-feed-loading">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="ai-feed-item skeleton">
                    <div className="skeleton-dot"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-line" style={{ width: '80%' }}></div>
                      <div className="skeleton-line" style={{ width: '50%' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : aiFeed.length > 0 ? (
              aiFeed.map((item, index) => (
                <div key={item.id || index} className="ai-feed-item">
                  <span className={`ai-feed-dot ${getStatusClass(item.status)}`}></span>
                  <div className="ai-feed-content">
                    <div className="ai-feed-action">{item.action || item.message}</div>
                    <div className="ai-feed-meta">
                      <span>{item.meta || item.company}</span>
                      <span className="ai-feed-time">{item.time || formatRelativeTime(item.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="ai-feed-empty">
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Budget Control */}
        <div className="ceo-widget widget-budget">
          <div className="ceo-widget-header">
            <div className="ceo-widget-title">💰 Budget Control</div>
            <span className="ceo-widget-action">Manage</span>
          </div>
          <div className="ceo-widget-body">
            {loadingBudget && !budgetData ? (
              <div className="budget-loading">
                <div className="skeleton-line" style={{ width: '60%', height: '24px' }}></div>
                <div className="skeleton-line" style={{ width: '40%' }}></div>
                <div className="skeleton-bar"></div>
              </div>
            ) : (
              <>
                <div className="budget-overview">
                  <div className="budget-spent">{formatCurrency(budget.spent)}</div>
                  <div className="budget-total">of {formatCurrency(budget.total)}</div>
                  <div className="budget-bar">
                    <div
                      className="budget-fill"
                      style={{ width: `${budgetPercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="budget-breakdown">
                  {budget.breakdown.length > 0 ? (
                    budget.breakdown.map((item, index) => (
                      <div key={index} className="budget-item">
                        <span className="budget-channel">{item.channel || item.name}</span>
                        <span className="budget-amount">{formatCurrency(item.amount || item.spent)}</span>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="budget-item">
                        <span className="budget-channel">No channel data</span>
                        <span className="budget-amount">-</span>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Approval Queue */}
        <div className="ceo-widget widget-queue">
          <div className="ceo-widget-header">
            <div className="ceo-widget-title">📋 Approval Queue</div>
            <span className="ceo-widget-badge">
              {loadingQueue ? '...' : `${approvalQueue.length} pending`}
            </span>
          </div>
          <div className="ceo-widget-body">
            {loadingQueue && approvalQueue.length === 0 ? (
              <div className="queue-loading">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="queue-item skeleton">
                    <div className="skeleton-content">
                      <div className="skeleton-line" style={{ width: '30%' }}></div>
                      <div className="skeleton-line" style={{ width: '70%' }}></div>
                      <div className="skeleton-line" style={{ width: '50%' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : approvalQueue.length > 0 ? (
              approvalQueue.map((item) => (
                <div key={item.id} className="queue-item">
                  <div className="queue-item-info">
                    <div className="queue-item-type">{item.type}</div>
                    <div className="queue-item-name">{item.name || item.title}</div>
                    <div className="queue-item-meta">
                      {item.company || item.tenant} • {item.meta || item.details}
                    </div>
                  </div>
                  <div className="queue-item-actions">
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => handleApprove(item.id)}
                      disabled={approveMutation.isLoading}
                    >
                      Approve
                    </Button>
                    <Button variant="ghost" size="xs">Review</Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="queue-empty">
                <p>No pending approvals</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="ceo-quick-actions">
        <Button variant="primary" icon="plus">New Campaign</Button>
        <Button variant="secondary" icon="edit">Create Content</Button>
        <Button variant="secondary" icon="mail">Send Email</Button>
        <Button variant="secondary" icon="chart">View Reports</Button>
      </div>
    </div>
  );
};

export default CEODashboard;
